import { PageName } from '@/router/routes/constants';
import dictionaryService from '@/core/translation/dictionary.service';
import authService from '@/core/auth/auth.service';
import { permissionForRoute } from '@/router/routes/client-routes/my-account.routes';
import permissionService from '@/project/auth/permission.service';
import { router } from 'spaRouter';
import serverContextService from '@/core/serverContext.service';
import { Feature } from '@/types/apiServerContract';
import Vue from 'vue';
import myAccountStore from '@/project/app/client-pages/my-account/my-account.store';

export interface ClientMenuItem {
    page?: string;
    url?: string;
    name: string;
    icon?: string;
    iconFill?: boolean; // as default on <CIcon> would be true, but we want a few icons to be false, usage would be something like this <CIcon name="..." fill="item.iconFill === undefined || item.iconFill === true"/>
    children?: ClientMenuItem[],
    isPrivate?: boolean;
    action?: () => void;
}

interface INavigationStoreState {
    myAccountNavigationGeneral: ClientMenuItem[];
    myAccountNavigationMisc: ClientMenuItem[];
    myAccountNavigationActions: ClientMenuItem[];
}
class NavigationStore {
    private state: INavigationStoreState = Vue.observable({
        myAccountNavigationGeneral: ([
            {
                name: dictionaryService.get('MyAccount.Profile'),
                icon: 'profile',
                page: PageName.MY_ACCOUNT_PROFILE
            },
            {
                name: dictionaryService.get('MyAccount.Navigation.OrderHistory'),
                icon: 'order-history',
                page: PageName.MY_ACCOUNT_ORDER_HISTORY
            },
            {
                name: dictionaryService.get('MyAccount.Navigation.ProductBacklog'),
                icon: 'stacked',
                page: PageName.MY_ACCOUNT_PRODUCT_BACKLOG
            },
            {
                name: dictionaryService.get('MyAccount.Navigation.Quotes'),
                icon: 'document',
                page: PageName.MY_ACCOUNT_QUOTES
            },
            {
                name: dictionaryService.get('MyAccount.Navigation.BlanketOrders'),
                icon: 'blanket-order',
                page: PageName.MY_ACCOUNT_BLANKET_ORDERS
            },
            {
                name: dictionaryService.get('MyAccount.Navigation.Bids'),
                icon: 'bid',
                page: PageName.MY_ACCOUNT_BIDS
            },
            serverContextService.isFeatureEnabled(Feature.Rma) && {
                name: dictionaryService.get('MyAccount.Navigation.RMAReturns'),
                icon: 'rma',
                page: PageName.MY_ACCOUNT_RMA_RETURN
            },
            {
                name: dictionaryService.get('MyAccount.Navigation.Ledger'),
                icon: 'ledger',
                page: PageName.MY_ACCOUNT_LEDGER
            },
            {
                name: dictionaryService.get('MyAccount.Navigation.ProductGuideConfiguration'),
                icon: 'productguide',
                page: PageName.MY_ACCOUNT_PRODUCT_GUIDE_CONFIGURATION
            }
        ].filter(<T>(n?: T): n is T => Boolean(n))) as ClientMenuItem[],
        myAccountNavigationMisc: [
            {
                name: dictionaryService.get('MyAccount.Navigation.Favourites'),
                icon: 'favorite-outline',
                page: PageName.MY_ACCOUNT_FAVOURITES
            },
            {
                name: dictionaryService.get('MyAccount.Navigation.AccountSettings'),
                icon: 'account-setting',
                page: PageName.MY_ACCOUNT_ACCOUNT_SETTINGS
            },
            {
                name: dictionaryService.get('MyAccount.Navigation.AddressBook'),
                icon: 'address-book',
                page: PageName.MY_ACCOUNT_ADDRESS_BOOK
            },
            {
                name: dictionaryService.get('MyAccount.Navigation.UserAdministration'),
                icon: 'profiles',
                page: PageName.MY_ACCOUNT_USER_ADMINISTRATION
            },
            {
                name: dictionaryService.get('MyAccount.Navigation.Impersonation'),
                icon: 'impersonate',
                page: PageName.MY_ACCOUNT_IMPERSONATION
            }
        ],
        myAccountNavigationActions: [
            {
                name: dictionaryService.get('MyAccount.Navigation.SignOut'),
                icon: 'login',
                action: () => {
                    authService.logOff();
                    router.push('/').catch(() => { });
                },
                isPrivate: true
            }
        ]
    });

    public get clientNavigationItems(): ClientMenuItem {
        return {
            name: dictionaryService.get('MyAccount.FrontPage.Title'),
            icon: 'profile',
            page: PageName.MY_ACCOUNT,
            children: [...this.myAccountNavigationGeneral, ...this.myAccountNavigationMisc, ...this.myAccountNavigationActions],
            isPrivate: true
        };
    }

    showNavElement(item: ClientMenuItem): boolean {
        if (item.isPrivate && !authService.isLoggedIn()) return false;
        if (item.page) {
            const permission = permissionForRoute(item.page);
            if (permission) {
                return permissionService.can(permission);
            }
        }
        return true;
    }

    public get myAccountNavigationGeneral() {
        return this.state.myAccountNavigationGeneral.filter(navItem => {
            if (navItem.page === PageName.MY_ACCOUNT_PRODUCT_GUIDE_CONFIGURATION) {
                return myAccountStore.hasProductGuideConfigurations;
            }
            return true;
        });
    }

    public get myAccountNavigationMisc() {
        return this.state.myAccountNavigationMisc;
    }

    public get myAccountNavigationActions() {
        return this.state.myAccountNavigationActions;
    }
}
export default new NavigationStore();
